// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Spinner.tsx
 * @input Uses React, StyleX, canvas rendering
 * @output Exports Spinner component, SpinnerProps, SpinnerSize, SpinnerShade types
 * @position Core implementation of spinner loading indicator
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Spinner/Spinner.doc.mjs
 * - /packages/core/src/Spinner/Spinner.test.tsx
 * - /packages/core/src/Spinner/index.ts
 * - /apps/storybook/stories/Spinner.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/Spinner/ (showcase blocks)
 */
import { useEffect, useId, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { durationVars, spacingVars } from "../theme/tokens.stylex.js";
import { useTheme } from "../theme/useTheme.js";
import { Text } from "../Text/Text.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";

// =============================================================================
// Constants
// =============================================================================

/** How much of the circle the active arc covers (as a fraction of 2π) */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const SPREAD = 0.75;
/** Where the active arc starts (as a fraction of 2π) */
const START_POINT = 1.5;
const SIZES = {
  sm: {
    diameter: 10,
    border: 2
  },
  md: {
    diameter: 14,
    border: 3
  },
  lg: {
    diameter: 18,
    border: 3
  },
  xl: {
    diameter: 28,
    border: 4
  }
};

// =============================================================================
// Animation
// =============================================================================

const rotation = "xqng64z-B";

// =============================================================================
// Styles
// =============================================================================

const styles = {
  wrapper: {
    k1xSpc: "x3nfvp2",
    kXwgrk: "xdt5ytf",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    $$css: true
  },
  spinner: {
    k1xSpc: "xwz0xwf",
    kgQiWS: "x1ku5rj1",
    kVQacm: "xb3r6kr",
    kXLuUW: "xxymvpz",
    $$css: true
  }
};

// =============================================================================
// Types
// =============================================================================

// =============================================================================
// Component
// =============================================================================

/**
 * An animated loading indicator. Available in three sizes and two color shades.
 *
 * @example
 * ```
 * <Spinner />
 * <Spinner size="sm" />
 * <Spinner size="lg" shade="onMedia" />
 * <Spinner label="Loading..." />
 * <Spinner aria-label="Loading data" />
 * ```
 */
export function Spinner({
  size = 'md',
  shade = 'default',
  label,
  xstyle,
  className,
  style,
  'aria-label': ariaLabel,
  'data-testid': testId,
  ref,
  ...restProps
}) {
  const canvasRef = useRef(null);
  const {
    tokens: themeTokens
  } = useTheme();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas == null) {
      return;
    }
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }
    const {
      border,
      diameter
    } = SIZES[size];
    const pixelRatio = window.devicePixelRatio || 1;

    // Resolve colors from theme tokens (useTheme handles light/dark resolution).
    // - default → accent ring on a track tuned to body luminance
    // - subtle  → secondary text color, less prominent
    // - onMedia → on-dark color, with a translucent track for photos/video
    // - inherit → the inherited currentColor, so the ring matches the parent's
    //   resolved foreground (e.g. a button's variant text color)
    const inheritedColor = shade === 'inherit' ? getComputedStyle(canvas).color : null;
    const activeColor = shade === 'inherit' ? inheritedColor : shade === 'onMedia' ? themeTokens['--color-on-dark'] : shade === 'subtle' ? themeTokens['--color-text-secondary'] : themeTokens['--color-accent'];
    // Track derives from --color-on-dark for onMedia (with a 30% alpha so the
    // ring reads against arbitrary backgrounds) and from --color-track for the
    // body-luminance shades. For inherit, the track is the same currentColor
    // drawn at reduced alpha via globalAlpha (see below). All branches are
    // fully theme-driven.
    const backgroundColor = shade === 'inherit' ? inheritedColor : shade === 'onMedia' ? `${themeTokens['--color-on-dark']}4D` : themeTokens['--color-track'];
    const cssSize = diameter + border * 2;

    // Round to an even number of device pixels so the center stays on a whole
    // pixel (avoids rotation jitter); keep CSS size pinned to cssSize (#2732).
    const rawFrameSize = Math.round(cssSize * pixelRatio);
    const frameSize = rawFrameSize + rawFrameSize % 2;
    const scale = frameSize / cssSize;
    const radius = diameter / 2 * scale;
    const lineWidth = border * scale;
    canvas.height = canvas.width = frameSize;
    canvas.style.width = canvas.style.height = cssSize + 'px';
    context.lineCap = 'round';
    context.lineWidth = lineWidth;
    const center = frameSize / 2;

    // Background circle (full ring, faded). For the inherit shade the track is
    // the same currentColor as the arc, so fade it via globalAlpha (the
    // computed color is an opaque rgb() string with no alpha channel to tweak).
    context.beginPath();
    context.arc(center, center, radius, 0, 2 * Math.PI);
    context.strokeStyle = backgroundColor;
    if (shade === 'inherit') {
      context.globalAlpha = 0.3;
    }
    context.stroke();
    context.globalAlpha = 1;

    // Active arc (partial ring, colored)
    context.beginPath();
    context.arc(center, center, radius, START_POINT * Math.PI, (START_POINT + SPREAD) % 2 * Math.PI);
    context.strokeStyle = activeColor;
    context.stroke();
  }, [shade, size, themeTokens]);
  const {
    border,
    diameter
  } = SIZES[size];
  const frameSize = diameter + border * 2;
  const hasLabel = label != null;
  const labelId = useId();

  // When a visible string label renders (and no explicit aria-label is set),
  // name the status element from the visible Text via aria-labelledby instead
  // of duplicating the same string as aria-label — the duplicate would be
  // announced twice by screen readers (WCAG 4.1.2).
  const namedByVisibleLabel = hasLabel && typeof label === 'string' && ariaLabel == null;

  // Resolve accessible name: explicit aria-label > string label > "Loading"
  const resolvedAriaLabel = ariaLabel ?? (typeof label === 'string' ? label : undefined) ?? 'Loading';
  const spinner = /*#__PURE__*/_jsx("span", {
    ref: hasLabel ? undefined : ref,
    role: "status",
    "aria-label": namedByVisibleLabel ? undefined : resolvedAriaLabel,
    "aria-labelledby": namedByVisibleLabel ? labelId : undefined,
    "data-testid": hasLabel ? undefined : testId,
    ...(hasLabel ? {} : restProps),
    ...mergeProps(hasLabel ? '' : themeProps('spinner', {
      size,
      shade
    }), stylex.props(styles.spinner, !hasLabel && xstyle), hasLabel ? undefined : className, {
      ...(hasLabel ? {} : style),
      width: frameSize,
      height: frameSize
    }),
    children: /*#__PURE__*/_jsx("canvas", {
      ref: canvasRef,
      ...{
        className: "xlp1x4z x1lliihq x1so62im x14qxm4i xnh0sag xa4qsjk x1ka1v4i x1esw782"
      }
    })
  });
  if (!hasLabel) {
    return spinner;
  }
  return /*#__PURE__*/_jsxs("div", {
    ref: ref,
    "data-testid": testId,
    ...restProps,
    ...mergeProps(themeProps('spinner', {
      size,
      shade
    }), stylex.props(styles.wrapper, xstyle), className, style),
    children: [spinner, typeof label === 'string' ? /*#__PURE__*/_jsx(Text, {
      id: labelId,
      type: "body",
      weight: "bold",
      children: label
    }) : label]
  });
}
Spinner.displayName = 'Spinner';